import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import CrossIcon from '../Icons/Cross.icon';
import Switch from '@mui/material/Switch';

export interface InputProps {
  mode?: 'default' | 'transparent';
  label?: React.ReactNode;
  onClick?: Function;
  onCopy?: (event: React.ClipboardEvent) => void;
  onClearClick?: Function;
  type?: 'text';
  right?: React.ReactNode;
  value?: string;
  placeholder?: string;
  isSwitchable?: boolean;
  switchValue?: boolean;
  onSwitchChange?: (checked: boolean) => void;
  leftSwitchLabel?: string;
  rightSwitchLabel?: string;
  onChange?: Function;
  onValid?: Function;
  onEnter?: Function;
  disabled?: boolean;
  readonly?: boolean;
  autoFocus?: boolean;
  prefix?: JSX.Element;
  maxLength?: number;
  validationFailureMessage?: string;
}

const StyledInputRight = styled.div``;

export const StyledInput = styled.input.attrs<{ maxLength?: string; disabled: boolean }>(props => ({
  maxlength: props.maxLength,
}))`
  border: none;
  background: transparent;
  width: 100%;
  height: 100%;
  font-size: inherit;

  ${props => props.disabled && `pointer-events: none;user-select: none;`};

  &,
  &:hover {
    outline: none;
  }
`;

const StyledInputWrapperNoValid = css`
  border-color: ${({ theme }) => theme.colors.RED.R400};
`;

const StyledInputWrapperFocus = css`
  border-color: ${({ theme }) => theme.colors.BLUE.B400};
`;

export const StyledInputDescription = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.RED.R400};
`;

export const StyledInputLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 22px;
  font-weight: 500;
`;

const StyledInputDefault = css`
  border: 1px solid ${({ theme }) => theme.colors.BORDER};
  background: ${({ theme }) => theme.colors.BG};
`;

const StyledInputClearButton = styled.div`
  display: flex;
  color: hsl(0, 0%, 80%);
  &:hover {
    color: hsl(0, 0%, 60%);
  }
  svg {
    fill: currentColor;
    width: 12px;
  }
`;

export const StyledInputWrapper = styled.div<{
  mode?: string;
  isFocus: boolean;
  isValid: boolean;
  clickable: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
}>`
  padding: 6px 10px;
  border-radius: 8px;
  transition: border-color 0.3s;
  display: flex;
  gap: 10px;
  align-items: center;

  ${props => props.mode === 'default' && StyledInputDefault};
  ${props => props.isDisabled && `opacity: 0.5; pointer-events: none;`};
  ${props => props.clickable && `cursor: pointer;`};
  ${props => props.isFocus && StyledInputWrapperFocus};
  ${props => !props.isValid && StyledInputWrapperNoValid};
`;
export const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const InputComponent = ({
  mode = 'default',
  value = '',
  label,
  type = 'text',
  right,
  placeholder,
  disabled: forceDisabled = false,
  readonly = false,
  onClick,
  onClearClick,
  isSwitchable = false,
  switchValue = false,
  onSwitchChange,
  leftSwitchLabel,
  rightSwitchLabel,
  onChange,
  onEnter,
  onCopy,
  prefix,
  autoFocus = false,
  maxLength = 150,
  validationFailureMessage = ''
}: InputProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocus, setFocus] = useState(false);

  const handleOnChange = (ev: any) => {
    const { target } = ev;

    onChange && onChange(target.value);
  };

  const handleSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onSwitchChange && onSwitchChange(checked);
  };

  const handleKeyDown = (ev: any) => {
    const { target } = ev;
    if (ev.key === 'Enter') {
      ev.preventDefault();
      onEnter && onEnter(target.value);
    }
  };

  const handleOnCopy = (event: React.ClipboardEvent) => {
    onCopy && onCopy(event);
  };

  const handleOnBlur = (ev: any) => {
    setFocus(false);
  };

  const handleOnFocus = () => {
    setFocus(true);
  };

  const isClearable = (!!onClearClick) && (!!value);

  const handleClearClick = (e: any) => {
    e.stopPropagation();
    onClearClick && onClearClick();
  };

  useLayoutEffect(() => {
    if (!autoFocus) return;
    inputRef.current && inputRef.current.focus();
  }, [autoFocus, inputRef.current]);

  return (
    <StyledInputContainer>
      {label &&
        <StyledInputLabel>
          {label}
          {isSwitchable && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {leftSwitchLabel && <span>{leftSwitchLabel}</span>}
              <Switch checked={switchValue} onChange={handleSwitchChange} />
              {rightSwitchLabel && <span>{rightSwitchLabel}</span>}
            </div>
          )}
        </StyledInputLabel>}

      <StyledInputWrapper
        mode={mode}
        isFocus={isFocus}
        isValid={!validationFailureMessage}
        clickable={!!onClick}
        isDisabled={forceDisabled}
        isReadOnly={readonly}
        onClick={() => onClick && onClick()}
      >
        {prefix}
        <StyledInput
          ref={inputRef}
          type={type}
          disabled={forceDisabled || readonly || !!onClick}
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={(e)=>handleKeyDown(e)}
          onChange={handleOnChange}
          onCopy={handleOnCopy}
          maxLength={maxLength}
        />
        {isClearable && (
          <StyledInputClearButton onClick={handleClearClick}>
            <CrossIcon />
          </StyledInputClearButton>
        )}

        {right && <StyledInputRight>{right}</StyledInputRight>}
      </StyledInputWrapper>

      {validationFailureMessage && (
        <StyledInputDescription>{validationFailureMessage}</StyledInputDescription>
      )}
    </StyledInputContainer>
  );
};

export default InputComponent;
